import { Directive, ElementRef, HostBinding, HostListener, Input, Renderer2, TemplateRef, ViewContainerRef } from '@angular/core';
import type { EmbeddedViewRef, OnDestroy } from '@angular/core';

@Directive({
  selector: '[tooltip], [tooltipClass]',
  standalone: true
})
export class TooltipDirective implements OnDestroy {
  private tooltipEl: HTMLDivElement | null = null;
  private tooltipContent: string | TemplateRef<unknown> = '';
  private tooltipView: EmbeddedViewRef<unknown> | null = null;
  private readonly offset = 10;
  private readonly viewportPadding = 12;
  private readonly mobileBreakpoint = 768;

  constructor(
    private host: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private viewContainer: ViewContainerRef
  ) {}

  @Input()
  set tooltip(value: string | TemplateRef<unknown> | null | undefined) {
    this.tooltipContent = value ?? '';
    this.titleAttr = '';
  }

  @Input() tooltipClass = '';

  @HostBinding('attr.title')
  titleAttr = '';

  @HostListener('mouseenter', ['$event'])
  @HostListener('focus', ['$event'])
  onShow(event: MouseEvent | FocusEvent): void {
    const content = this.tooltipContent instanceof TemplateRef
      ? this.tooltipContent
      : this.formatTooltipText(this.tooltipContent);
    if (!content) {
      return;
    }

    this.createTooltip(content);
    this.updatePosition(event);
  }

  @HostListener('mousemove', ['$event'])
  onMove(event: MouseEvent): void {
    this.updatePosition(event);
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  onHide(): void {
    this.destroyTooltip();
  }

  ngOnDestroy(): void {
    this.destroyTooltip();
  }

  private createTooltip(content: string | TemplateRef<unknown>): void {
    this.destroyTooltip();

    const tooltip = this.renderer.createElement('div') as HTMLDivElement;
    tooltip.className = `wc-tooltip ${this.tooltipClass || ''}`.trim();
    if (content instanceof TemplateRef) {
      this.tooltipView = this.viewContainer.createEmbeddedView(content);
      this.tooltipView.detectChanges();
      for (const node of this.tooltipView.rootNodes) {
        this.renderer.appendChild(tooltip, node);
      }
    } else {
      tooltip.textContent = content;
    }

    this.renderer.setStyle(tooltip, 'position', 'fixed');
    this.renderer.setStyle(tooltip, 'z-index', '10000');
    this.renderer.setStyle(tooltip, 'pointer-events', 'none');
    this.renderer.setStyle(tooltip, 'max-width', '300px');
    this.renderer.setStyle(tooltip, 'padding', '6px 8px');
    this.renderer.setStyle(tooltip, 'border-radius', '4px');
    this.renderer.setStyle(tooltip, 'background', 'rgba(15, 23, 42, 0.95)');
    this.renderer.setStyle(tooltip, 'color', '#fff');
    this.renderer.setStyle(tooltip, 'font-size', '12px');
    this.renderer.setStyle(tooltip, 'line-height', '1.35');
    this.renderer.setStyle(tooltip, 'white-space', 'normal');
    this.renderer.setStyle(tooltip, 'box-shadow', '0 6px 16px rgba(0, 0, 0, 0.25)');

    this.renderer.appendChild(document.body, tooltip);
    this.tooltipEl = tooltip;
  }

  private updatePosition(event: MouseEvent | FocusEvent): void {
    if (!this.tooltipEl) {
      return;
    }

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const tooltipWidth = this.tooltipEl.offsetWidth;
    const tooltipHeight = this.tooltipEl.offsetHeight;
    const anchorX = event instanceof MouseEvent ? event.clientX : hostRect.left + hostRect.width / 2;
    const anchorY = event instanceof MouseEvent ? event.clientY : hostRect.top;

    let left = anchorX + this.offset;
    let top = anchorY + this.offset;

    if (viewportWidth <= this.mobileBreakpoint) {
      left = (viewportWidth - tooltipWidth) / 2;
      top = hostRect.bottom + this.offset;
    }

    const maxLeft = viewportWidth - tooltipWidth - this.viewportPadding;
    const maxTop = viewportHeight - tooltipHeight - this.viewportPadding;
    left = Math.max(this.viewportPadding, Math.min(left, maxLeft));
    top = Math.max(this.viewportPadding, Math.min(top, maxTop));

    this.renderer.setStyle(this.tooltipEl, 'left', `${Math.round(left)}px`);
    this.renderer.setStyle(this.tooltipEl, 'top', `${Math.round(top)}px`);
  }

  private destroyTooltip(): void {
    this.tooltipView?.destroy();
    this.tooltipView = null;
    if (!this.tooltipEl) {
      return;
    }

    this.renderer.removeChild(document.body, this.tooltipEl);
    this.tooltipEl = null;
  }

  private formatTooltipText(value: string): string {
    return value
      .replace(/<br\s*\/?>(\n)?/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
      .replace(/[ \t]+/g, ' ')
      .replace(/\n\s+/g, '\n')
      .trim();
  }

}
